import { createHash } from 'crypto';
import { join, dirname } from 'path';
import fs from 'fs-extra';
import chalk from 'chalk';
import { logger } from './logger.js';

// ============================================================================
// Init State - Track initialization progress for resume capability
// ============================================================================

const INIT_STATE_FILE = 'init-state.json';
const SMARTSTACK_DIR = '.smartstack';

export interface InitFileRecord {
  /** Relative path from projectDir */
  relativePath: string;
  /** SHA-256 hash of the file content at creation time */
  hash: string;
  /** ISO timestamp when the file was created by init */
  createdAt: string;
}

export interface InitStepState {
  /** Step identifier */
  id: string;
  /** Display name for logging */
  name: string;
  /** Current status */
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  /** ISO timestamp when step started */
  startedAt?: string;
  /** ISO timestamp when step completed */
  completedAt?: string;
  /** Error message if failed */
  error?: string;
  /** Files created by this step, with their hashes */
  files: InitFileRecord[];
}

export interface InitState {
  /** Schema version for forward compatibility */
  schemaVersion: 1;
  /** The project config used for this init */
  projectConfig: Record<string, unknown>;
  /** CLI version that started the init */
  cliVersion: string;
  /** ISO timestamp when init started */
  startedAt: string;
  /** ISO timestamp when last updated */
  updatedAt: string;
  /** Ordered list of steps with their status */
  steps: InitStepState[];
}

// ============================================================================
// Hashing
// ============================================================================

/** Compute SHA-256 hash of string or Buffer content */
export function computeHash(content: string | Buffer): string {
  return createHash('sha256').update(content).digest('hex');
}

/** Compute SHA-256 hash of a file on disk */
export async function computeFileHash(filePath: string): Promise<string> {
  const content = await fs.readFile(filePath);
  return computeHash(content);
}

// ============================================================================
// Safe File Writing
// ============================================================================

export type SafeWriteAction = 'created' | 'overwritten' | 'skipped_modified' | 'skipped_identical';

export interface SafeWriteResult {
  action: SafeWriteAction;
  hash: string;
}

/**
 * Writes a file only if it does not exist or has not been modified by the user.
 *
 * @param filePath - Absolute path to the file
 * @param content - Content to write
 * @param knownHash - Hash of the file when init originally created it (from state)
 * @returns The action taken and the hash of the content
 */
export async function safeWriteFile(
  filePath: string,
  content: string | Buffer,
  knownHash?: string,
): Promise<SafeWriteResult> {
  const newHash = computeHash(content);

  if (await fs.pathExists(filePath)) {
    const existingHash = await computeFileHash(filePath);

    // Content is identical - no action needed
    if (existingHash === newHash) {
      return { action: 'skipped_identical', hash: newHash };
    }

    // We have a known hash from a previous init run
    if (knownHash) {
      if (existingHash === knownHash) {
        // File was NOT modified by user (still matches what init created).
        // Safe to overwrite with new content.
        await fs.ensureDir(dirname(filePath));
        await fs.writeFile(filePath, content);
        return { action: 'overwritten', hash: newHash };
      }
      // File was modified by user - DO NOT OVERWRITE
      return { action: 'skipped_modified', hash: existingHash };
    }

    // No known hash (first run or legacy) and content differs - conservative, skip
    return { action: 'skipped_modified', hash: existingHash };
  }

  // File does not exist - create it
  await fs.ensureDir(dirname(filePath));
  await fs.writeFile(filePath, content);
  return { action: 'created', hash: newHash };
}

// ============================================================================
// State Persistence
// ============================================================================

/** Load init state from .smartstack/init-state.json */
export async function loadInitState(projectDir: string): Promise<InitState | null> {
  const statePath = join(projectDir, SMARTSTACK_DIR, INIT_STATE_FILE);
  try {
    if (await fs.pathExists(statePath)) {
      const content = await fs.readFile(statePath, 'utf-8');
      return JSON.parse(content) as InitState;
    }
  } catch {
    // Corrupt or unreadable - will be treated as no state
  }
  return null;
}

/** Save init state to .smartstack/init-state.json */
export async function saveInitState(projectDir: string, state: InitState): Promise<void> {
  const dir = join(projectDir, SMARTSTACK_DIR);
  await fs.ensureDir(dir);
  const statePath = join(dir, INIT_STATE_FILE);
  await fs.writeFile(statePath, JSON.stringify(state, null, 2), 'utf-8');
}

const INIT_STEPS: Array<{ id: string; name: string }> = [
  { id: 'config', name: 'Configuration files' },
  { id: 'backend', name: 'Backend structure' },
  { id: 'frontend', name: 'Frontend structure' },
  { id: 'docker', name: 'Docker configuration' },
  { id: 'git', name: 'Git initialization' },
  { id: 'ralph', name: 'Ralph configuration' },
];

/** Create a fresh init state with all steps pending */
export function createFreshInitState(
  projectConfig: Record<string, unknown>,
  cliVersion: string,
): InitState {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    projectConfig,
    cliVersion,
    startedAt: now,
    updatedAt: now,
    steps: INIT_STEPS.map(({ id, name }) => ({
      id,
      name,
      status: 'pending' as const,
      files: [],
    })),
  };
}

// ============================================================================
// Step Execution with Checkpointing
// ============================================================================

/**
 * Executes a step with checkpoint save before/after.
 * Skips already completed steps.
 */
export async function executeStep(
  state: InitState,
  stepId: string,
  projectDir: string,
  dryRun: boolean,
  stepFn: () => Promise<void>,
): Promise<void> {
  const step = state.steps.find(s => s.id === stepId);
  if (!step) throw new Error(`Unknown step: ${stepId}`);

  const stepIndex = state.steps.indexOf(step);
  const totalSteps = state.steps.length;

  // Skip completed steps
  if (step.status === 'completed') {
    logger.step(stepIndex + 1, totalSteps, `${step.name} ${chalk.gray('✓ Already completed')}`);
    return;
  }

  // Mark as in_progress and save
  step.status = 'in_progress';
  step.startedAt = new Date().toISOString();
  step.error = undefined;
  state.updatedAt = new Date().toISOString();
  if (!dryRun) {
    await saveInitState(projectDir, state);
  }

  logger.step(stepIndex + 1, totalSteps, `${step.name}...`);

  try {
    await stepFn();

    // Mark as completed
    step.status = 'completed';
    step.completedAt = new Date().toISOString();
    state.updatedAt = new Date().toISOString();
    if (!dryRun) {
      await saveInitState(projectDir, state);
    }
  } catch (error) {
    step.status = 'failed';
    step.error = error instanceof Error ? error.message : String(error);
    state.updatedAt = new Date().toISOString();
    if (!dryRun) {
      await saveInitState(projectDir, state);
    }
    throw error; // Re-throw for the main handler
  }
}

// ============================================================================
// State Helpers
// ============================================================================

/**
 * Find a known hash for a file from a previous init run.
 * Used by safeWriteFile to determine if a file was modified by the user.
 */
export function findKnownHash(
  state: InitState,
  stepId: string,
  relativePath: string,
): string | undefined {
  const step = state.steps.find(s => s.id === stepId);
  if (!step) return undefined;
  const record = step.files.find(f => f.relativePath === relativePath);
  return record?.hash;
}

/**
 * Record a file hash in the state for a given step.
 */
export function recordFile(
  state: InitState,
  stepId: string,
  relativePath: string,
  hash: string,
): void {
  const step = state.steps.find(s => s.id === stepId);
  if (!step) return;

  const existing = step.files.find(f => f.relativePath === relativePath);
  if (existing) {
    existing.hash = hash;
  } else {
    step.files.push({
      relativePath,
      hash,
      createdAt: new Date().toISOString(),
    });
  }
}

/**
 * Log the result of a safeWriteFile operation.
 */
export function logSafeWriteResult(relativePath: string, result: SafeWriteResult): void {
  switch (result.action) {
    case 'created':
      logger.success(`Created ${relativePath}`);
      break;
    case 'overwritten':
      logger.info(`Updated ${relativePath}`);
      break;
    case 'skipped_modified':
      logger.warning(`Skipped ${relativePath} (modified by user)`);
      break;
    case 'skipped_identical':
      // Silent - no need to log identical files
      break;
  }
}

/** Check if an init state has incomplete steps */
export function hasIncompleteSteps(state: InitState): boolean {
  return state.steps.some(s => s.status !== 'completed');
}

/** Count completed steps */
export function countCompletedSteps(state: InitState): number {
  return state.steps.filter(s => s.status === 'completed').length;
}

/** Display resume summary */
export function displayResumeSummary(state: InitState): void {
  const completed = countCompletedSteps(state);
  const total = state.steps.length;

  logger.header('SmartStack Project Initialization (Resuming)');
  logger.info(`Resuming from previous attempt (${state.startedAt.split('T')[0]})`);
  logger.info(`Steps completed: ${completed}/${total}`);
  console.log();

  for (let i = 0; i < state.steps.length; i++) {
    const step = state.steps[i];
    const num = `[${i + 1}/${total}]`;
    switch (step.status) {
      case 'completed':
        console.log(chalk.gray(`  ${num} ${step.name}`) + chalk.green('  ✓ Already completed'));
        break;
      case 'failed':
      case 'in_progress':
        console.log(chalk.yellow(`  ${num} ${step.name}`) + chalk.yellow('  → Resuming...'));
        break;
      case 'pending':
        console.log(chalk.gray(`  ${num} ${step.name}`) + chalk.gray('    Pending'));
        break;
    }
  }
  console.log();
}
